/**
 * Award Feedback Constants
 * 
 * Defines the selection reasons available when a client awards a job to a vendor.
 * These reasons are used for vendor analytics and performance insights.
 */

/**
 * Represents a selection reason with its display label and description
 */
export interface SelectionReasonOption {
  value: string;
  label: string;
  description: string;
}

/**
 * Selection reasons available when awarding a job
 * These explain why the client chose the winning vendor
 */
export const SELECTION_REASONS: SelectionReasonOption[] = [
  {
    value: 'best_price',
    label: 'Best Price',
    description: 'Competitive pricing was the deciding factor',
  },
  {
    value: 'fastest_delivery',
    label: 'Fastest Delivery',
    description: 'Offered the quickest turnaround time',
  },
  {
    value: 'profile_portfolio',
    label: 'Profile and Portfolio',
    description: 'Business description and gallery',
  },
  {
    value: 'ratings_reviews',
    label: 'Ratings and Reviews',
    description: 'Positive ratings/reviews influenced the decision',
  },
  {
    value: 'previous_experience',
    label: 'Previous Experience',
    description: 'Prior successful work with this vendor',
  },
  {
    value: 'capabilities',
    label: 'Capabilities',
    description: 'Business capabilities are best-fit',
  },
  {
    value: 'certifications',
    label: 'Certifications',
    description: 'Vendor holds preferred or required certifications',
  },
  {
    value: 'only_bidder',
    label: 'Only Bidder',
    description: 'This was the only vendor who submitted a bid',
  },
] as const;

/**
 * Type for selection reason values
 */
export type SelectionReasonValue = typeof SELECTION_REASONS[number]['value'];

/**
 * Maximum length for additional comments
 */
export const FEEDBACK_COMMENTS_MAX_LENGTH = 500;
